import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NO_TYPING_PING, shouldPingTyping } from "./typing-throttle";
import { useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useChatStore } from "@/stores/chat-store";
import {
  HeartbeatPresenceDocument,
  MarkConversationReadDocument,
  SendChatMessageDocument,
  SetTypingDocument,
} from "@/graphql/generated/operations";
import { logApplicationFailure } from "@/lib/user-error";

// Presence heartbeat cadence for the conversation currently in view.
// Must stay below genesis' presenceTtl (ChatProperties default: 30s) so
// the other side keeps seeing us online while the window is open.
const PRESENCE_HEARTBEAT_MS = 20_000;

interface ChatViewProps {
  conversationId: string;
}


export function ChatView({ conversationId }: ChatViewProps) {
  const intl = useIntl();
  const myId = useChatStore((s) => s.myId);
  const messagesRaw = useChatStore((s) => s.messages.get(conversationId));
  const messages = useMemo(() => messagesRaw ?? [], [messagesRaw]);
  const addMessage = useChatStore((s) => s.addMessage);
  const typingSet = useChatStore((s) => s.typing.get(conversationId));
  const readReceipts = useChatStore((s) => s.readReceipts.get(conversationId));
  // The live subscription + history seeding live in ChatDock's
  // ConversationLiveBridge (single source per conversation). This window
  // only renders from the store and sends; it reads the bridge's error
  // flag to show the "live updates unavailable" banner.
  const liveError = useChatStore((s) => s.liveErrors.has(conversationId));

  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [sendMessage] = useMutation(SendChatMessageDocument);
  const [markRead] = useMutation(MarkConversationReadDocument);
  const [sendTyping] = useMutation(SetTypingDocument);
  // Throttle window for the typing:true ping. Comfortably inside genesis's presence TTL, so the peer
  // never sees the indicator flicker, while collapsing a burst of keystrokes into one mutation.
  const lastTypingPingRef = useRef(NO_TYPING_PING);
  const [heartbeat] = useMutation(HeartbeatPresenceDocument);

  // Publish our presence while this window is open. ChatWindow only
  // mounts ChatView for visible (non-minimized) conversations, so this
  // scopes "online" to the chat the user is actually looking at. Best-
  // effort: a failed beat just means we look offline until the next one.
  useEffect(() => {
    const beat = (online: boolean) =>
      void heartbeat({ variables: { input: { conversationId, online } } }).catch(() => {});
    beat(true);
    const timer = setInterval(() => beat(true), PRESENCE_HEARTBEAT_MS);
    return () => {
      clearInterval(timer);
      beat(false);
    };
  }, [conversationId, heartbeat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!myId) return;
    const last = messages[messages.length - 1];
    if (!last || last.senderId === myId) return;
    markRead({
      variables: { input: { conversationId, lastReadMessageId: last.id } },
    }).catch(() => {
      logApplicationFailure("chat.mark-read");
    });
  }, [messages, conversationId, myId, markRead]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    try {
      // Append the sent message from the mutation result instead of waiting for
      // the subscription echo - so it shows immediately even when live updates
      // are down. addMessage dedupes by id, so the later echo is a no-op.
      const { data } = await sendMessage({
        variables: { input: { conversationId, content: text } },
      });
      const msg = data?.sendChatMessage;
      if (msg) {
        addMessage({
          id: msg.id,
          conversationId: msg.conversationId,
          senderId: msg.senderId,
          content: msg.content,
          sentAt: msg.sentAt,
        });
      }
    } catch {
      logApplicationFailure("chat.send-message");
      setInput(text); // restore so the user does not lose their text
      return;
    }
    void sendTyping({ variables: { input: { conversationId, typing: false } } });
  }, [input, conversationId, sendMessage, sendTyping, addMessage]);

  const handleInputChange = useCallback(
    (value: string) => {
      setInput(value);
      clearTimeout(typingTimerRef.current);
      if (value.length > 0) {
        // Only the trailing typing:false was debounced; typing:true fired on EVERY keystroke — about 240
        // mutations for a 40-word message, each carrying a session lookup, an upstream fetch and a Redis
        // write. genesis's presence TTL means one ping per interval is enough to stay "typing".
        const now = Date.now();
        if (shouldPingTyping(now, lastTypingPingRef.current)) {
          lastTypingPingRef.current = now;
          void sendTyping({ variables: { input: { conversationId, typing: true } } });
        }
        typingTimerRef.current = setTimeout(() => {
          lastTypingPingRef.current = NO_TYPING_PING;
          void sendTyping({ variables: { input: { conversationId, typing: false } } });
        }, 3000);
      } else {
        lastTypingPingRef.current = NO_TYPING_PING;
        void sendTyping({ variables: { input: { conversationId, typing: false } } });
      }
    },
    [conversationId, sendTyping],
  );

  const typingUsers = typingSet ? [...typingSet].filter((id) => id !== myId) : [];

  // Show "Seen" only when our own message is the latest in the thread and
  // another participant's read pointer has reached it. Message ids are
  // unsorted UUIDs, so compare positions in the (time-ordered) list.
  const seen = useMemo(() => {
    const last = messages[messages.length - 1];
    if (!myId || !last || last.senderId !== myId || !readReceipts) return false;
    const lastIdx = messages.length - 1;
    const posById = new Map(messages.map((m, i) => [m.id, i]));
    for (const [personId, receipt] of readReceipts) {
      if (personId === myId) continue;
      const pos = posById.get(receipt.lastReadMessageId);
      if (pos !== undefined && pos >= lastIdx) return true;
    }
    return false;
  }, [messages, myId, readReceipts]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {liveError && (
        <div
          className="border-b border-ink/10 px-4 py-1.5 text-center text-[10px] tracking-wider text-red-600"
          role="status"
        >
          <FormattedMessage
            id="chat.liveUpdatesUnavailable"
            defaultMessage="Live updates unavailable"
          />
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="space-y-2">
          {messages.map((msg) => {
            const isMine = msg.senderId === myId;
            return (
              <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[75%]">
                  <div
                    className={`rounded-lg px-3 py-2 text-sm leading-relaxed ${
                      // Partner bubbles use the smycles background color
                      // (tenant-themed --color-page); the hairline ring keeps
                      // them visible when that color is near-white.
                      isMine ? "bg-ink text-on-ink" : "bg-page text-ink ring-1 ring-ink/5"
                    }`}
                  >
                    {msg.content}
                  </div>
                  <p
                    className={`mt-0.5 text-[10px] text-ink/40 ${
                      isMine ? "text-right" : ""
                    }`}
                  >
                    {new Date(msg.sentAt).toLocaleTimeString(intl.locale, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
            );
          })}
          {seen && (
            <p className="text-right text-[10px] text-ink/40">
              <FormattedMessage id="chat.seen" defaultMessage="Seen" />
            </p>
          )}
          <div ref={messagesEndRef} />
        </div>

        {typingUsers.length > 0 && (
          <div className="mt-2 flex items-center gap-2 text-xs text-ink/40">
            <span className="flex gap-0.5">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink/40 [animation-delay:0ms]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink/40 [animation-delay:150ms]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink/40 [animation-delay:300ms]" />
            </span>
            <FormattedMessage id="chat.typing" defaultMessage="typing…" />
          </div>
        )}
      </div>

      <div className="border-t border-ink/10 px-4 py-3">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
            placeholder={intl.formatMessage({
              id: "chat.writeMessage",
              defaultMessage: "Write a message…",
            })}
            aria-label={intl.formatMessage({
              id: "chat.messageInput",
              defaultMessage: "Message input",
            })}
            className="flex-1 rounded border border-ink/15 bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
          />
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!input.trim()}
            aria-label={intl.formatMessage({
              id: "chat.sendMessage",
              defaultMessage: "Send message",
            })}
            className="flex h-9 w-9 items-center justify-center rounded bg-ink text-on-ink transition-opacity hover:opacity-80 disabled:opacity-20"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
